import { useState } from "react";
import { Input } from "../../../components/inputs/Input";
import IndeterminateCheckbox from "../../../components/inputs/IndeterminantCheckbox";
import CompactThumbUploader from "../../../components/CompactThumbUploader";
import CustomSelect from "../../../components/customSelect";
import { Item } from "../../../types/item";
import { usdFormat } from "../../../utils";
import { PlusIcon, XCircleIcon } from "@heroicons/react/24/solid";

export interface Category {
  name: string;
  hide: boolean;
  items: number[];
  image: string;
}

interface CategoryManagerProps {
  categories: Category[];
  setCategories: (categories: Category[]) => void;
  items: Item[];
  itemsAllVendors: Item[];
  selectedEvent: string | undefined;
  selectedVendor: string | undefined;
  imageOptions: { value: string; label: string; display_name: string; image_base64: string }[];
}

// Default category names for new categories
const DEFAULT_CATEGORY_NAMES = [
  "Beer",
  "Liquor",
  "Wine",
  "NonAl",
  "Merchandise",
  "Food",
  "Category 7",
  "Category 8",
  "Category 9",
  "Category 10",
];

// Convert category object (from DB) to array format
export function categoryObjectToArray(category: any): Category[] {
  if (!category) {
    // Return default 6 categories
    return DEFAULT_CATEGORY_NAMES.slice(0, 6).map((name) => ({
      name,
      hide: false,
      items: [],
      image: "",
    }));
  }

  // Determine how many categories exist by finding all cat_N keys
  let maxIndex = 0;
  for (const key of Object.keys(category)) {
    const match = key.match(/^cat_(\d+)$/);
    if (match) {
      maxIndex = Math.max(maxIndex, parseInt(match[1], 10));
    }
  }

  // Ensure at least 6 for backwards compatibility with old records
  maxIndex = Math.max(maxIndex, 6);

  const categories: Category[] = [];
  for (let i = 1; i <= maxIndex; i++) {
    categories.push({
      name: category[`cat_${i}`] ?? DEFAULT_CATEGORY_NAMES[i - 1] ?? `Category ${i}`,
      hide: !!category[`cat_${i}_hide`],
      items: parseItemsIds(category[`cat_${i}_itemsIds`]),
      image: category[`cat_${i}_image`] ?? "",
    });
  }

  return categories;
}

// Convert array format back to category object (for DB)
export function categoryArrayToObject(categories: Category[]): any {
  const result: any = {};

  categories.forEach((cat, index) => {
    const i = index + 1;
    result[`cat_${i}`] = cat.name || "";
    result[`cat_${i}_hide`] = cat.hide;
    result[`cat_${i}_itemsIds`] = cat.items.map(String);
    result[`cat_${i}_image`] = cat.image || "";
  });

  return result;
}

// Parse items IDs from various formats
function parseItemsIds(value: any): number[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === "string" ? parseInt(v, 10) : v)).filter((v) => !isNaN(v));
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((v) => parseInt(v.trim(), 10))
      .filter((v) => !isNaN(v));
  }
  return [];
}

// Get default categories for create mode
export function getDefaultCategories(): Category[] {
  return DEFAULT_CATEGORY_NAMES.slice(0, 6).map((name) => ({
    name,
    hide: false,
    items: [],
    image: "",
  }));
}

async function blobToDataURL(blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

function dataUrlToRawBase64(dataUrl: string): string {
  const [, raw = ""] = dataUrl.split(",", 2);
  return raw;
}

export function CategoryManager({
  categories,
  setCategories,
  items,
  itemsAllVendors,
  selectedEvent,
  selectedVendor,
  imageOptions,
}: CategoryManagerProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const fromIndex = dragIndex;
    if (fromIndex === null || fromIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const updated = [...categories];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(dropIndex, 0, moved);
    setCategories(updated);

    // Update active index to follow the moved tab
    if (activeIndex === fromIndex) {
      setActiveIndex(dropIndex);
    } else if (fromIndex < activeIndex && dropIndex >= activeIndex) {
      setActiveIndex(activeIndex - 1);
    } else if (fromIndex > activeIndex && dropIndex <= activeIndex) {
      setActiveIndex(activeIndex + 1);
    }

    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleCategoryNameChange = (index: number, name: string) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], name };
    setCategories(updated);
  };

  const handleCategoryHideChange = (index: number, hide: boolean) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], hide };
    setCategories(updated);
  };

  const handleCategoryImageChange = (index: number, image: string) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], image };
    setCategories(updated);
  };

  const handleCategoryThumbReady = async (index: number, blob: Blob) => {
    const dataUrl = await blobToDataURL(blob);
    const rawBase64 = dataUrlToRawBase64(dataUrl);
    handleCategoryImageChange(index, rawBase64);
  };

  const handleItemToggle = (categoryIndex: number, itemId: number, checked: boolean) => {
    const updated = [...categories];
    const currentItems = [...updated[categoryIndex].items];

    if (checked) {
      if (!currentItems.includes(itemId)) {
        currentItems.push(itemId);
      }
    } else {
      const idx = currentItems.indexOf(itemId);
      if (idx !== -1) {
        currentItems.splice(idx, 1);
      }
    }

    updated[categoryIndex] = { ...updated[categoryIndex], items: currentItems };
    setCategories(updated);
  };

  const handleAddCategory = () => {
    const newIndex = categories.length + 1;
    const newName = DEFAULT_CATEGORY_NAMES[categories.length] ?? `Category ${newIndex}`;
    setCategories([...categories, { name: newName, hide: false, items: [], image: "" }]);
    // Switch to the new tab
    setActiveIndex(categories.length);
  };

  const handleRemoveCategory = (index: number) => {
    if (categories.length <= 1) return; // Keep at least one category

    const updated = categories.filter((_, i) => i !== index);
    setCategories(updated);

    // Adjust active index if needed
    if (activeIndex >= updated.length) {
      setActiveIndex(updated.length - 1);
    } else if (activeIndex > index) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const getCategoryLabel = (cat: Category, index: number): string => {
    const name = cat.name?.trim() || `Category ${index + 1}`;
    return cat.hide ? `${name} (Hidden)` : name;
  };

  return (
    <div>
      {/* Tab Headers */}
      <div className="flex flex-wrap border-b border-gray-300 items-center">
        {categories.map((cat, index) => (
          <div
            key={index}
            className={`relative ${
              dragOverIndex === index ? "border-l-2 border-primary-500" : ""
            } ${dragIndex === index ? "opacity-50" : ""}`}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <button
              type="button"
              className={`w-full sm:w-auto pl-3 pr-6 py-2 -mb-px text-sm font-medium rounded-t text-center cursor-grab active:cursor-grabbing ${
                index === activeIndex
                  ? "bg-gray-100 border-t border-l border-r border-gray-300 text-gray-900"
                  : "bg-white text-gray-500 hover:text-gray-700 border border-gray-300"
              }`}
              onClick={() => setActiveIndex(index)}
            >
              {getCategoryLabel(cat, index)}
            </button>
            {/* Remove button - red X circle in top right corner inside tab */}
            {categories.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveCategory(index);
                }}
                className="absolute top-1 right-1 text-red-500 hover:text-red-700"
                title="Remove Category"
              >
                <XCircleIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        {/* Add Category Button */}
        <button
          type="button"
          onClick={handleAddCategory}
          className="w-1/2 sm:w-auto px-3 py-2 -mb-px text-sm font-medium rounded-t text-center bg-green-50 text-green-700 hover:bg-green-100 border border-green-300 flex items-center gap-1 justify-center"
          title="Add Category"
        >
          <PlusIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Add</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="border border-gray-300 border-t-0 rounded-b bg-gray-100 p-4">
        {categories.map((cat, index) => (
          <div key={index} className={index === activeIndex ? "block" : "hidden"}>
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-4 sm:col-span-3">
                <Input
                  label={`Category ${index + 1} Name`}
                  name={`cat_${index + 1}`}
                  maxLength={15}
                  value={cat.name}
                  onChange={(value) => handleCategoryNameChange(index, String(value))}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                  label="Hide?"
                  name={`cat_${index + 1}_hide`}
                  defaultChecked={cat.hide}
                  onChange={(checked: any) => handleCategoryHideChange(index, !!checked)}
                />
              </div>
              <div className="col-span-6 sm:col-span-2 bg-gray-300 p-2 rounded flex items-center gap-3">
                <div className="flex items-center gap-2">
                  {cat.image && (
                    <div className="relative w-[50px] h-[50px] flex-shrink-0">
                      <img
                        src={`data:image/webp;base64,${cat.image}`}
                        width={50}
                        height={50}
                        alt="category thumb"
                        className="rounded border border-slate-200 object-cover w-[50px] h-[50px]"
                      />
                      <button
                        type="button"
                        onClick={() => handleCategoryImageChange(index, "")}
                        aria-label="Remove image"
                        title="Remove image"
                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#f04345] text-white
                          flex items-center justify-center shadow ring-1 ring-white/60 hover:opacity-90
                          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f04345] text-xs"
                      >
                        &times;
                      </button>
                    </div>
                  )}
                  <CompactThumbUploader
                    onReady={(blob) => handleCategoryThumbReady(index, blob)}
                    size={100}
                    title="Upload"
                  />
                </div>
                <div className="flex-1">
                  <CustomSelect
                    title="Library"
                    name={`cat_${index + 1}_image_library`}
                    value={cat.image}
                    onChange={(v) => handleCategoryImageChange(index, v)}
                    options={imageOptions}
                  />
                </div>
              </div>

              {/* Items Selection */}
              {selectedEvent && selectedVendor && (
                <div className="col-span-6 grid grid-cols-6 gap-5 bg-gray-100 p-2 rounded">
                  {items.map((item: Item) => (
                    <div key={item.id} className="col-span-2 sm:col-span-1">
                      <IndeterminateCheckbox
                        label={`${item.name} (${usdFormat.format(item.price / 100)})${item.is_active === false ? " (inactive)" : ""}`}
                        name={`cat_${index + 1}_items_${item.id}`}
                        defaultChecked={cat.items.includes(item.id)}
                        onChange={(checked: any) => handleItemToggle(index, item.id, !!checked)}
                      />
                    </div>
                  ))}
                  {itemsAllVendors.length > 0 && (
                    <div className="col-span-6 grid grid-cols-6 gap-5 bg-gray-300 p-2 rounded">
                      <label className="col-span-6 block text-sm font-medium text-gray-700">
                        Event Items
                      </label>
                      {itemsAllVendors.map((item: Item) => (
                        <div key={item.id} className="col-span-2 sm:col-span-1">
                          <IndeterminateCheckbox
                            label={`${item.name} (${usdFormat.format(item.price / 100)})${item.is_active === false ? " (inactive)" : ""}`}
                            name={`cat_${index + 1}_items_${item.id}`}
                            defaultChecked={cat.items.includes(item.id)}
                            onChange={(checked: any) => handleItemToggle(index, item.id, !!checked)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Hidden inputs for form submission */}
      {categories.map((cat, index) => (
        <div key={`hidden-${index}`}>
          <input type="hidden" name={`cat_${index + 1}`} value={cat.name} />
          <input type="hidden" name={`cat_${index + 1}_hide`} value={String(cat.hide)} />
          <input type="hidden" name={`cat_${index + 1}_items`} value={cat.items.join(",")} />
          <input type="hidden" name={`cat_${index + 1}_image`} value={cat.image} />
        </div>
      ))}
      <input type="hidden" name="category_count" value={categories.length} />
    </div>
  );
}
